import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  getUnreadMessages,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "../../services";

interface NotificationStoreState extends NotificationState {
  isOpen: boolean;
  addNotification: (
    notification: Omit<NotificationItem, "id" | "read">
  ) => void;
  markAsRead: (salesConsultancyHashid: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  toggleOpen: () => void;
  close: () => void;
  open: () => void;
  loadUnreadNotifications: () => Promise<void>;
}

export const useNotificationStore = create<NotificationStoreState>()(
  persist(
    (set) => ({
      notifications: [],
      unreadCount: 0,
      isOpen: false,

      addNotification: (notification) =>
        set((state) => {
          const existingNotification = state.notifications.find(
            (n) =>
              n.roomUuid === notification.roomUuid &&
              n.message === notification.message &&
              new Date(n.timestamp).getTime() ===
                new Date(notification.timestamp).getTime()
          );

          if (existingNotification) {
            return state;
          }

          const newNotification = {
            ...notification,
            id: notification.salesConsultancyHashid,
            read: false,
          };

          const updatedNotifications = [
            newNotification,
            ...state.notifications,
          ];

          const limitedNotifications = updatedNotifications.slice(0, 20);

          return {
            notifications: limitedNotifications,
            unreadCount: state.unreadCount + 1,
          };
        }),

      markAsRead: async (salesConsultancyHashid: string) => {
        try {
          await markNotificationAsRead(salesConsultancyHashid);
        } catch (e) {
          console.error("Error marking notification as read:", e);
        }
        set((state: any) => {
          const updatedNotifications = state.notifications.map(
            (notification: any) =>
              notification.salesConsultancyHashid === salesConsultancyHashid &&
              !notification.read
                ? { ...notification, read: true }
                : notification
          );
          const newUnreadCount =
            state.unreadCount > 0 ? state.unreadCount - 1 : 0;
          return {
            notifications: updatedNotifications,
            unreadCount: newUnreadCount,
          };
        });
      },

      markAllAsRead: async () => {
        const getState = (useNotificationStore as any).getState;
        const unread = getState().notifications.filter((n: any) => !n.read);
        await markAllNotificationsAsRead(
          unread.map((n: any) => n.salesConsultancyHashid)
        );
        set((state: any) => ({
          notifications: state.notifications.map((notification: any) => ({
            ...notification,
            read: true,
          })),
          unreadCount: 0,
        }));
      },

      clearNotifications: () =>
        set({
          notifications: [],
          unreadCount: 0,
        }),

      toggleOpen: () =>
        set((state) => ({
          isOpen: !state.isOpen,
        })),

      close: () => set({ isOpen: false }),

      open: () => set({ isOpen: true }),

      loadUnreadNotifications: async () => {
        try {
          const unread = await getUnreadMessages();
          if (Array.isArray(unread)) {
            set(() => ({
              notifications: unread.map((msg: any) => ({
                message: msg.message,
                sender: {
                  id: msg.sender.id,
                  name: `${msg.sender.first_name} ${msg.sender.last_name}`,
                  avatar: msg.sender.avatar,
                },
                salesConsultancyHashid: msg.salesConsultancyHashid,
                roomUuid: msg.roomUuid,
                timestamp: new Date(msg.timestamp),
                type: "message",
                id: msg.salesConsultancyHashid,
                read: msg.isRead,
              })),
              unreadCount: unread.filter((n: any) => !n.isRead).length,
            }));
          }
        } catch (e) {
          console.error("Error loading unread notifications:", e);
        }
      },
    }),
    {
      name: "notifications-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          timestamp:
            n.timestamp instanceof Date
              ? n.timestamp.toISOString()
              : n.timestamp,
        })),
        unreadCount: state.unreadCount,
      }),
      onRehydrateStorage: () => (state) => {
        if (state && state.notifications) {
          state.notifications = state.notifications.map((n) => ({
            ...n,
            timestamp: new Date(n.timestamp),
          }));
        }
      },
    }
  )
);

export default useNotificationStore;
